import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../firebase";
import { resolveProfilePhotoFromUser } from "../utils/profilePhotoResolver";
import { stopWebCallHeartbeat, syncWebCallHeartbeat } from "./webCallHeartbeat";
import {
  addNativeCallEndedListener,
  addNativeCallRemoteTerminalListener,
  addNativeCallRemoteHangupSignalListener,
  closeNativeCallRoom,
  openNativeCallRoom,
  updateNativeCallRoomProfile,
} from "../plugins/NativeLivoraCall";
import {
  resolveReturnContext,
  removeReturnContext,
  markNativeCallReturnStarted,
  mapCallReturnToastMessage,
  CALL_RETURN_FALLBACK_URL,
} from "./nativeCallReturnContext";
import { logNativeCallOpenSuccess, logNativeCallOpenFailed } from "./callRoute";
import { logCallStartupEvent } from "./callStartupTrace";
import {
  getPreparedNativeCallData,
  beginNativeCallPreparation,
  clearNativeCallPreparation,
} from "./nativeCallPreparation";
import "./NativeCallRoomPage.css";

const TERMINAL_STATUSES = new Set([
  "ended",
  "cancelled",
  "canceled",
  "rejected",
  "failed",
  "missed",
  "timeout",
]);

/**
 * Phase 4B / 4D: Native Call Room entry. Post-call return restores each
 * user's pre-call page via returnContext (not hardcoded /home).
 */
export default function NativeCallRoomPage() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState("loading");
  const [ending, setEnding] = useState(false);

  const startedRef = useRef(false);
  const heartbeatActiveRef = useRef(false);
  // Phase 4D-4D1: smallest explicit ownership flag for the JS->native
  // heartbeat handoff. Starts false (Web owns the heartbeat while
  // waiting/ringing and before the native Activity has opened). Set true
  // only once LivoraNativeCallRoomActivity has actually opened for the male
  // participant, at which point the native Handler-driven heartbeat
  // (NativeCallHeartbeatClient -> heartbeatCall) becomes the sole owner and
  // syncHeartbeatForStatus below becomes a no-op for the rest of this call.
  const nativeHeartbeatOwnedRef = useRef(false);
  // Phase 1 (unify Android calls to native): the retry/end-call handlers for
  // the native-open failure UI are defined inside the data-loading effect
  // (they need its `cancelled` closure and async prep), but the render
  // (below the effect) needs to invoke them from button taps - bridge with
  // refs, same pattern already used for role/uid above.
  const retryOpenNativeCallRoomRef = useRef(null);
  const endNativeCallRef = useRef(null);
  const endHandledRef = useRef(false);
  const callCompletedRef = useRef(false);
  const roleRef = useRef(null);
  const uidRef = useRef(auth.currentUser?.uid || "");

  useEffect(() => {
    let cancelled = false;

    if (!callId) {
      logNativeCallOpenFailed({ callId, reason: "missing_call_id" });
      setStatus("error");
      return undefined;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      logNativeCallOpenFailed({ callId, reason: "no_current_user" });
      setStatus("error");
      return undefined;
    }
    const uid = currentUser.uid;
    uidRef.current = uid;

    function stopHeartbeatIfActive(reason) {
      if (heartbeatActiveRef.current) {
        stopWebCallHeartbeat(callId, reason);
        heartbeatActiveRef.current = false;
      }
    }

    function syncHeartbeatForStatus(callStatus) {
      if (nativeHeartbeatOwnedRef.current) {
        // Native Activity already owns the heartbeat for this call - never
        // restart the Web timer underneath it (would create sustained
        // duplicate heartbeats once the WebView resumes ticking).
        return;
      }
      const role = roleRef.current;
      if (role === "male" && callStatus === "ongoing") {
        heartbeatActiveRef.current = true;
        syncWebCallHeartbeat({
          callId,
          role,
          status: callStatus,
          onServerEnded: () => {
            console.log("[NATIVE_CALL_ROOM] heartbeat_server_ended", { callId });
          },
        });
      } else {
        stopHeartbeatIfActive("status_not_ongoing_or_not_male");
      }
    }

    /**
     * Idempotent post-call restore. Does not await endCall.
     * Never navigate(-1) / exitApp.
     */
    function restorePreCallPage(trigger, reason) {
      if (endHandledRef.current || callCompletedRef.current) {
        console.log(
          `[callReturn] duplicate_restore_ignored trigger=${trigger || "unknown"}`
        );
        return;
      }
      endHandledRef.current = true;
      callCompletedRef.current = true;
      clearNativeCallPreparation(callId);

      console.log("[NATIVE_CALL_ROOM] native_call_ended_received", {
        callId,
        reason: reason || trigger,
      });

      if (!cancelled) {
        setEnding(true);
      }
      console.log("[NATIVE_CALL_ROOM] fallback_hidden_after_native_end", { callId });

      stopHeartbeatIfActive(`restore_${trigger || "unknown"}`);

      const { context, source } = resolveReturnContext({
        uid,
        callId,
        stateReturnContext: location.state?.returnContext,
        legacyReturnTo: location.state?.returnTo,
      });
      const targetUrl = context?.url || CALL_RETURN_FALLBACK_URL;
      if (source === "fallback") {
        console.log(
          `[callReturn] fallback_home reason=resolved_fallback callId=${callId}`
        );
      }

      markNativeCallReturnStarted();
      console.log(
        `[callReturn] restoring trigger=${trigger || "unknown"} url=${targetUrl}`
      );

      // Native already finishes itself on native_remote_terminal — do not
      // request a duplicate closeNativeCallRoom finish.
      if (trigger !== "native_remote_terminal") {
        void closeNativeCallRoom({
          callId,
          reason: `restore_${trigger || "unknown"}`,
        }).then((result) => {
          console.log("[NATIVE_CALL_ROOM] close_result", result);
        });
      }

      if (!cancelled) {
        navigate(targetUrl, {
          replace: true,
          state: {
            callReturnScroll: {
              x: context?.scrollX || 0,
              y: context?.scrollY || 0,
            },
            callReturnMessage: mapCallReturnToastMessage(trigger, reason),
          },
        });
      }

      removeReturnContext(uid, callId);
    }

    function endNativeCall(reason) {
      restorePreCallPage("local_end", reason || "unknown");

      // Phase N1: Native already dispatched the primary endCall request via
      // NativeCallCoordinator before this event ever reached React. This
      // call is a duplicate-safe fallback only (endCall.js's "already
      // ended" branch makes repeat invocations harmless) - it must never
      // delay or block the return navigation above.
      void (async () => {
        try {
          const functions = getFunctions(undefined, "us-central1");
          const endCall = httpsCallable(functions, "endCall");
          await endCall({ callId, reason: `native_${reason}` });
          console.log("[NATIVE_CALL_ROOM] endCall_success owner=react_fallback", { callId });
        } catch (e) {
          console.log("[NATIVE_CALL_ROOM] endCall_failed owner=react_fallback", String(e?.message || e));
        }
      })();
    }
    endNativeCallRef.current = endNativeCall;

    async function openNativeCallRoomForCall(call) {
      let role = null;
      let otherUserId = null;
      if (call.maleUid === uid) {
        role = "male";
        otherUserId = call.femaleUid;
      } else if (call.femaleUid === uid) {
        role = "female";
        otherUserId = call.maleUid;
      }

      if (!role || !otherUserId) {
        logNativeCallOpenFailed({ callId, reason: "not_a_participant" });
        setStatus("error");
        return;
      }
      roleRef.current = role;
      const traceRole = role === "male" ? "caller" : "receiver";

      const userName =
        currentUser.displayName || currentUser.email?.split("@")[0] || "User";

      // Phase N2A: consume whatever ringing-time preparation already
      // produced (started in CallListener.js / OutgoingCallPage.js) instead
      // of re-fetching sequentially. Falls back to starting it now (still
      // parallel, just later) if preparation is missing or expired.
      let prepared = getPreparedNativeCallData(callId, uid);
      if (!prepared) {
        prepared = beginNativeCallPreparation({ callId, uid, userName, role: traceRole });
      }

      const [bootstrap, idTokenResult, nativeAuthResult] = await Promise.all([
        prepared?.bootstrapPromise || Promise.resolve(null),
        prepared?.idTokenPromise || Promise.resolve(null),
        prepared?.nativeAuthPromise || Promise.resolve(null),
      ]);
      if (cancelled || callCompletedRef.current) return;

      if (!bootstrap?.token || !bootstrap?.appID) {
        logNativeCallOpenFailed({ callId, reason: "token_fetch_failed" });
        setStatus("error");
        return;
      }

      const roomId = String(bootstrap.roomId || callId);
      const ownStreamId = `${roomId}_${uid}_main`;

      const firebaseIdToken = idTokenResult?.token || "";
      const firebaseIdTokenExpirationMs = idTokenResult?.expirationMs || 0;
      console.log("[NATIVE_CALL_ROOM] firebase_id_token_ready", {
        present: !!firebaseIdToken,
        hasExpiration: firebaseIdTokenExpirationMs > 0,
      });
      // Phase N2A1: nativeAuthResult reflects the REAL outcome - either a
      // live session was already reused (no custom token was ever fetched)
      // or a real sign-in completed/failed. customToken is only non-null
      // when a fresh sign-in was actually attempted.
      const nativeAuthReady = nativeAuthResult?.authReady === true;
      const nativeFirebaseCustomToken = nativeAuthResult?.customToken || "";
      console.log(
        `[NATIVE_CALL_ROOM] native_firebase_session_prepared authReady=${nativeAuthReady} reason=${nativeAuthResult?.reason || "unknown"}`
      );

      if (cancelled || callCompletedRef.current) return;

      console.log("[NATIVE_CALL_ROOM] data_contract_ready", {
        callId,
        roomId,
        role,
        otherUserId,
        ownStreamId,
        firebaseIdTokenPresent: !!firebaseIdToken,
        nativeAuthReady,
      });

      if (role === "male") {
        console.log("[heartbeatSync] ownership=web reason=pre_native");
      }
      syncHeartbeatForStatus(String(call.status || "").toLowerCase());

      const openParams = {
        callId,
        roomId,
        userId: uid,
        userName,
        role,
        otherUserId,
        // Phase N2A section 7: profile photo/name are no longer on the
        // blocking path - resolved after openNativeCallRoom below and
        // pushed in via updateNativeCallRoomProfile without ever delaying
        // the Activity opening or first video frame.
        otherUserName: "",
        otherUserPhotoUrl: "",
        ownStreamId,
        zegoAppId: String(bootstrap.appID),
        zegoToken: String(bootstrap.token),
      };
      if (firebaseIdToken) {
        openParams.firebaseIdToken = firebaseIdToken;
        openParams.firebaseIdTokenExpirationMs = firebaseIdTokenExpirationMs;
      }
      // Phase N2A1: always tell the Activity who to expect, whether or not
      // a fresh custom token was fetched - a reused live session (no token
      // at all) still needs nativeFirebaseExpectedUid so the Activity's own
      // live-match check can trust it immediately. Only include the custom
      // token when a real sign-in was actually attempted.
      openParams.nativeFirebaseExpectedUid = uid;
      if (nativeFirebaseCustomToken) {
        openParams.nativeFirebaseCustomToken = nativeFirebaseCustomToken;
      }

      logCallStartupEvent(callId, "native_activity_open_requested", traceRole);
      const result = await openNativeCallRoom(openParams);
      console.log("[NATIVE_CALL_ROOM] open_result", result);

      if (cancelled || callCompletedRef.current) return;

      if (result.opened) {
        logNativeCallOpenSuccess({ callId });
        setStatus("opened");
        clearNativeCallPreparation(callId);
        if (role === "male") {
          // Native Activity is now the sole heartbeat owner - stop the Web
          // timer explicitly rather than waiting for the WebView to be
          // paused. If openNativeCallRoom had failed instead, this branch
          // is skipped entirely and syncHeartbeatForStatus above keeps the
          // Web heartbeat as the running fallback.
          nativeHeartbeatOwnedRef.current = true;
          console.log("[heartbeatSync] ownership=native reason=native_room_opened");
          stopHeartbeatIfActive("native_handoff");
          console.log("[heartbeatSync] web heartbeat stopped reason=native_handoff");
        }

        // First-frame priority: resolve the other participant's display
        // name/photo only now, in the background, and push it in - never
        // part of the critical path above.
        void (async () => {
          try {
            const otherProfileSnap = await getDoc(doc(db, "publicProfiles", otherUserId));
            const otherProfileData = otherProfileSnap.exists() ? otherProfileSnap.data() : {};
            const otherUserName = String(
              otherProfileData?.name || otherProfileData?.displayName || otherProfileData?.username || ""
            );
            const otherUserPhotoUrl = await resolveProfilePhotoFromUser(
              otherProfileData,
              otherProfileData?.gender
            );
            if (cancelled || callCompletedRef.current) return;
            void updateNativeCallRoomProfile({ callId, otherUserName, otherUserPhotoUrl });
          } catch (e) {
            console.log(
              "[NATIVE_CALL_ROOM] other_user_photo_resolve_failed",
              String(e?.message || e)
            );
          }
        })();
      } else {
        logNativeCallOpenFailed({ callId, reason: result.reason || "open_failed" });
        // Do not let a failed open retry reuse the same (possibly stale)
        // prepared token/bootstrap - Retry should fetch everything fresh.
        clearNativeCallPreparation(callId);
        setStatus("error");
      }
    }

    async function handleFirstSnapshot(call) {
      if (callCompletedRef.current || endHandledRef.current) {
        return;
      }

      const callStatus = String(call.status || "").toLowerCase();
      if (TERMINAL_STATUSES.has(callStatus)) {
        restorePreCallPage("remote_terminal", `terminal_${callStatus}`);
        return;
      }

      await openNativeCallRoomForCall(call);
    }

    async function handleRetryOpenNativeCallRoom() {
      if (cancelled || callCompletedRef.current || endHandledRef.current) {
        return;
      }
      setStatus("loading");
      try {
        const snap = await getDoc(doc(db, "calls", callId));
        if (cancelled || callCompletedRef.current) return;
        if (!snap.exists()) {
          logNativeCallOpenFailed({ callId, reason: "call_not_found" });
          setStatus("error");
          return;
        }
        const call = snap.data() || {};
        const callStatus = String(call.status || "").toLowerCase();
        if (TERMINAL_STATUSES.has(callStatus)) {
          restorePreCallPage("remote_terminal", `terminal_${callStatus}`);
          return;
        }
        await openNativeCallRoomForCall(call);
      } catch (e) {
        if (cancelled || callCompletedRef.current) return;
        logNativeCallOpenFailed({ callId, reason: "retry_failed" });
        setStatus("error");
      }
    }
    retryOpenNativeCallRoomRef.current = handleRetryOpenNativeCallRoom;

    console.log(`[NATIVE_CALL_ROOM] page_start callId=${callId}`);

    // Register native events before Activity open (async in first snapshot).
    const endedListenerHandle = addNativeCallEndedListener(({ callId: endedCallId, reason }) => {
      if (endedCallId && endedCallId !== callId) return;
      void endNativeCall(reason || "unknown");
    });
    const remoteTerminalHandle = addNativeCallRemoteTerminalListener((payload) => {
      if (payload?.callId && payload.callId !== callId) return;
      // No endCall — server/peer already wrote terminal status.
      restorePreCallPage(
        "native_remote_terminal",
        payload?.endedReason || payload?.status || "remote_terminal"
      );
    });
    // Phase N1.1: instant remote-hangup hint via validated Zego signaling.
    // Never calls endCall and never navigates to Web CallRoom - purely the
    // same restorePreCallPage return-navigation used by every other remote
    // path. Any later duplicate Firestore terminal event is already a no-op
    // via callCompletedRef/endHandledRef inside restorePreCallPage.
    const remoteHangupSignalHandle = addNativeCallRemoteHangupSignalListener((payload) => {
      if (payload?.callId && payload.callId !== callId) return;
      restorePreCallPage(
        "realtime_hangup",
        payload?.reason || payload?.source || "zego_custom_command"
      );
    });

    const unsubscribe = onSnapshot(
      doc(db, "calls", callId),
      (snap) => {
        if (cancelled) return;

        if (!snap.exists()) {
          logNativeCallOpenFailed({ callId, reason: "call_not_found" });
          setStatus("error");
          return;
        }

        const call = snap.data() || {};
        const callStatus = String(call.status || "").toLowerCase();

        if (!startedRef.current) {
          startedRef.current = true;
          void handleFirstSnapshot(call);
          return;
        }

        if (callCompletedRef.current || endHandledRef.current) {
          return;
        }

        syncHeartbeatForStatus(callStatus);

        if (TERMINAL_STATUSES.has(callStatus)) {
          restorePreCallPage("remote_terminal", `terminal_${callStatus}`);
        }
      },
      (error) => {
        console.log("[NATIVE_CALL_ROOM] call_doc_listen_failed", String(error?.message || error));
        if (!cancelled) {
          logNativeCallOpenFailed({ callId, reason: "call_doc_read_failed" });
          setStatus("error");
        }
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
      endedListenerHandle?.remove?.();
      remoteTerminalHandle?.remove?.();
      remoteHangupSignalHandle?.remove?.();
      stopHeartbeatIfActive("native_call_room_unmount");
    };
  }, [callId, navigate, location.state?.returnContext, location.state?.returnTo]);

  // Phase 1 (unify Android calls to native): native-open failure policy.
  // Never silently falls back to /call/:callId - Retry re-attempts the
  // native open with fresh call data; End Call goes through the same real
  // endCall() path (and returnContext restore) as every other end-of-call
  // exit, never a bare local close.
  const handleRetryTapped = () => {
    retryOpenNativeCallRoomRef.current?.();
  };

  const handleEndCallTapped = () => {
    endNativeCallRef.current?.("open_failed");
  };

  if (ending) {
    return (
      <div className="native-call-room-fallback native-call-room-fallback--ending">
        <div className="native-call-room-fallback-ending-text">Ending call…</div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="native-call-room-fallback">
        <div className="native-call-room-fallback-box" dir="rtl">
          <div className="native-call-room-fallback-status">
            تعذر بدء المكالمة. يمكنك إعادة المحاولة أو إنهاء المكالمة.
          </div>
          <div className="native-call-room-fallback-actions">
            <button
              type="button"
              className="native-call-room-fallback-retry-btn"
              onClick={handleRetryTapped}
            >
              إعادة المحاولة
            </button>
            <button
              type="button"
              className="native-call-room-fallback-end-btn"
              onClick={handleEndCallTapped}
            >
              إنهاء المكالمة
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <div className="native-call-room-fallback" />;
}
